import { Stack, Typography } from '@mui/material';
import { RecruiterLocationSearch } from 'features/co-pilot/co-pilot.types';
import { OptionType } from 'features/ui/base-components/pai-auto-complete';
import PaiCheckbox from 'features/ui/base-components/pai-checkbox';
import PaiMenuItem from 'features/ui/base-components/pai-menu-item';
import PaiSearchSelector from 'features/ui/base-components/pai-search-selector';
import PaiSelect from 'features/ui/base-components/pai-select';
import _ from 'lodash';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { makeStyles } from 'tss-react/mui';
import ConstraintChip from './constraint-chip';

interface Props {
  recruiterLocationConstraints: RecruiterLocationSearch;
  disabled?: boolean;

  handleSelectRecruiterLocationConstraint(searchType: 'geo' | 'zip'): (value: string) => () => void;

  handleRemoveRecruiterLocationConstraint(searchType: 'geo' | 'zip', urn: string): () => void;

  handleRecruiterLocationConstraintQueryChange(searchType: 'geo' | 'zip'): (event: React.ChangeEvent<HTMLInputElement>) => void;

  searchRecruiterLocationKeyUp(searchType: 'geo' | 'zip'): () => void;

  searchRecruiterLocationKeyDown(): void;

  handleRecruiterLocationAreaChange(event: React.ChangeEvent<HTMLInputElement>): void;

  handleResetLinkedinSearch?(): void;

  handleResetLocationSettings?(): void;
}

const RecruiterLocation: React.FC<Props> = ({
  recruiterLocationConstraints,
  disabled,
  handleSelectRecruiterLocationConstraint,
  handleRemoveRecruiterLocationConstraint,
  handleRecruiterLocationConstraintQueryChange,
  searchRecruiterLocationKeyUp,
  searchRecruiterLocationKeyDown,
  handleRecruiterLocationAreaChange,
  handleResetLinkedinSearch,
  handleResetLocationSettings,
}) => {
  const { t } = useTranslation();
  const { classes } = useStyles();
  const [locationType, setLocationType] = useState('zip');

  const areaOptions: OptionType[] = [
    { label: t('within1Mi'), value: '1' },
    { label: t('within5Mi'), value: '5' },
    { label: t('within10Mi'), value: '10' },
    { label: t('within25Mi'), value: '25' },
    { label: t('within50Mi'), value: '50' },
    { label: t('within100Mi'), value: '100' },
    { label: t('within200Mi'), value: '200' },
  ];

  const handleLocationTypeChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newType = event.target.value;

    if (newType !== locationType) {
      setLocationType(newType);
      handleResetLinkedinSearch?.();
      handleResetLocationSettings?.();
    }
  };

  return (
    <Stack gap={3}>
      <Stack direction={'row'} gap={3}>
        <PaiCheckbox label={t('areaSearch')} checked={locationType === 'zip'} value={'zip'} onChange={handleLocationTypeChange} />
        <PaiCheckbox label={t('locationSearch')} checked={locationType === 'region'} value={'region'} onChange={handleLocationTypeChange} />
      </Stack>
      {locationType === 'region' ? (
        <Stack gap={1}>
          <PaiSearchSelector
            placeholder={t('enterLocationHere')}
            value={recruiterLocationConstraints['geo'].query}
            selectedValues={recruiterLocationConstraints['geo'].selected.map((c) => c.value)}
            options={recruiterLocationConstraints['geo'].options}
            onChange={handleRecruiterLocationConstraintQueryChange('geo')}
            onKeyUp={searchRecruiterLocationKeyUp('geo')}
            onKeyDown={searchRecruiterLocationKeyDown}
            onMenuItemClick={handleSelectRecruiterLocationConstraint('geo')}
            multiselect={true}
            style={{ maxWidth: '600px' }}
            disabled={disabled || recruiterLocationConstraints['geo'].loading}
            loading={recruiterLocationConstraints['geo'].loading}
          />
          {recruiterLocationConstraints['geo'].isEmpty ? <Typography className={classes.errorText}>{t('noLocationsFound')}</Typography> : null}
          {!_.isEmpty(recruiterLocationConstraints['geo'].selected) ? (
            <Stack direction={'row'} gap={1} flexWrap={'wrap'} sx={{ maxWidth: '700px' }}>
              {recruiterLocationConstraints['geo'].selected.map((item) => (
                <ConstraintChip
                  key={item.value}
                  label={item.label}
                  titleType={item.titleType}
                  handleRemoveRecruiterConstraint={handleRemoveRecruiterLocationConstraint('geo', item.value)}
                />
              ))}
            </Stack>
          ) : null}
        </Stack>
      ) : locationType === 'zip' ? (
        <Stack gap={2}>
          <Stack gap={1}>
            <PaiSearchSelector
              label={t('zip')}
              placeholder={t('enterZipHere')}
              value={recruiterLocationConstraints['zip'].query}
              selectedValues={recruiterLocationConstraints['zip'].selected.map((c) => c.value)}
              options={recruiterLocationConstraints['zip'].options}
              onChange={handleRecruiterLocationConstraintQueryChange('zip')}
              onKeyUp={searchRecruiterLocationKeyUp('zip')}
              onKeyDown={searchRecruiterLocationKeyDown}
              onMenuItemClick={handleSelectRecruiterLocationConstraint('zip')}
              multiselect={true}
              style={{ maxWidth: '600px' }}
              disabled={disabled || recruiterLocationConstraints['zip'].loading}
              loading={recruiterLocationConstraints['zip'].loading}
            />
            {recruiterLocationConstraints['zip'].isEmpty ? <Typography className={classes.errorText}>{t('noLocationsFound')}</Typography> : null}
            {!_.isEmpty(recruiterLocationConstraints['zip'].selected) ? (
              <Stack direction={'row'} gap={1} flexWrap={'wrap'} sx={{ maxWidth: '700px' }}>
                {recruiterLocationConstraints['zip'].selected.map((item) => (
                  <ConstraintChip
                    key={item.value}
                    label={item.label}
                    titleType={item.titleType}
                    handleRemoveRecruiterConstraint={handleRemoveRecruiterLocationConstraint('zip', item.value)}
                  />
                ))}
              </Stack>
            ) : null}
          </Stack>
          <PaiSelect
            label={t('area')}
            style={{ maxWidth: '600px' }}
            value={recruiterLocationConstraints.area}
            onChange={handleRecruiterLocationAreaChange}
          >
            {areaOptions.map((option) => (
              <PaiMenuItem key={option.value} value={option.value}>
                {option.label}
              </PaiMenuItem>
            ))}
          </PaiSelect>
        </Stack>
      ) : null}
    </Stack>
  );
};

const useStyles = makeStyles()((theme) => ({
  errorText: {
    fontSize: theme.typography.pxToRem(14),
    color: theme.palette.error.main,
    padding: '0 8px',
  },
}));

export default RecruiterLocation;
